import React from "react";

import { useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { formatISO } from "date-fns/esm";
import { NavLink } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import BillingStatus from "@/pages/Clients/ClientDetails/Index/components/BillingStatus";
import NewClientDialog from "@/pages/Clients/ClientList/components/NewClientDialog";
import { colors } from "@/theme/overrides/colors";

import {
  sortByStatus,
  sortByClientNameDesc,
  sortByClientNameAsc,
} from "./utils/sort";
import { useSidebarContext } from "../../Sidebar";
import HoverBase from "../ClickBase";

const useStyles = makeStyles()((theme: Theme) => ({
  users: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },

  user: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(5),
  },

  link: {
    textDecoration: "none",
    color: theme.palette.common.black,
  },

  avatar: {
    width: "40px",
    height: "40px",
  },
}));

const StyledButtonBase = withStyles(ButtonBase, (theme: Theme) => ({
  root: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: theme.spacing(2, 2),
    "&:hover": {
      backgroundColor: "#EDF0F5",
      cursor: "pointer",
    },
  },
}));

const clientListQuery = graphql(`
  query ClientList($currentDate: ISO8601Date!) {
    clients {
      nodes {
        id
        profile {
          id
          fullName
          avatarUrl(variant: THUMB)
        }
        programForDate(date: $currentDate) {
          id
          name
        }
        status
      }
    }
  }
`);

const ClientList: React.FC = () => {
  const { classes } = useStyles();
  const { dispatch } = useSidebarContext();
  const currentDate = formatISO(new Date(), { representation: "date" });
  const { data, refetch } = useQuery(clientListQuery, {
    variables: { currentDate: currentDate },
  });
  const clients = data?.clients.nodes;
  const [filterValue, setFilterValue] = React.useState<
    "" | { name?: string | undefined; value: unknown } | undefined
  >("");
  const [allClients, setAllClients] = React.useState<typeof clients>([]);
  const [searchText, setSearcText] = React.useState("");
  const [isAddClientDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const openAddClientDialog = () => setIsAddDialogOpen(true);
  const closeAddClientDialog = () => setIsAddDialogOpen(false);

  React.useEffect(() => {
    if (clients) setAllClients([...clients].sort(sortByStatus));
  }, [clients]);

  function handleSort(
    e: SelectChangeEvent<
      "" | { name?: string | undefined; value: unknown } | undefined
    >
  ) {
    const filter = e.target.value as
      | ""
      | { name?: string | undefined; value: unknown }
      | undefined;
    setFilterValue(filter);
    if (allClients === undefined) return;
    switch (filter) {
      case "asc":
        return setAllClients([...allClients].sort(sortByClientNameAsc));
      case "desc":
        return setAllClients([...allClients].sort(sortByClientNameDesc));
      case "status":
        return setAllClients([...allClients].sort(sortByStatus));
    }
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setSearcText(event.target.value);
    const keyword = event.target.value.toLowerCase();
    if (keyword !== "" && allClients !== undefined) {
      const newClients = allClients.filter((client: any) => {
        const fullName = client?.profile?.fullName ?? "";
        return fullName.toLocaleLowerCase().includes(keyword);
      });
      setAllClients(newClients);
    } else {
      setFilterValue("");
      setAllClients(clients);
    }
  }

  return (
    <>
      <HoverBase componentName="clients" expandLink="/clients">
        <div>
          <Box
            paddingX={2}
            flexGrow={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            position="relative"
          >
            <Button
              onClick={() => {
                dispatch({ type: "close-component" });
                openAddClientDialog();
              }}
            >
              Add new client
            </Button>
            <FormControl variant="outlined" size="small">
              <Select
                value={filterValue}
                inputProps={{ "aria-label": "Without label" }}
                onChange={handleSort}
              >
                <MenuItem value={""} disabled>
                  Sort by
                </MenuItem>
                <MenuItem value={"status"}>Status</MenuItem>
                <MenuItem value={"asc"}>Ascending</MenuItem>
                <MenuItem value={"desc"}>Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            paddingX={2}
            marginY={2}
            flexGrow={1}
            display="flex"
            alignItems="center"
          >
            <TextField
              value={searchText}
              onChange={handleSearch}
              fullWidth
              size="small"
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon htmlColor={colors.grey} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </div>

        <div className={classes.users}>
          {allClients?.length === 0 ? (
            <div className={classes.user}>
              <Typography>No user found</Typography>
            </div>
          ) : (
            allClients?.map((client: any) => (
              <NavLink
                to={`/clients/${client?.id}/profile`}
                key={client?.id}
                className={classes.link}
                onClick={() =>
                  dispatch({
                    type: "close-component",
                    payload: "clients",
                  })
                }
              >
                <StyledButtonBase>
                  <Avatar
                    src={client?.profile?.avatarUrl ?? undefined}
                    className={classes.avatar}
                    alt={"User"}
                    imgProps={{
                      loading: "lazy",
                    }}
                  />
                  <Box
                    marginLeft="12px"
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    textAlign="start"
                  >
                    <Typography variant="body1">
                      {client?.profile?.fullName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {client?.programForDate?.name ?? "No plan"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignSelf="stretch" marginLeft="auto">
                    <BillingStatus status={client?.status} />
                  </Box>
                </StyledButtonBase>
              </NavLink>
            ))
          )}
        </div>
      </HoverBase>

      <NewClientDialog
        open={isAddClientDialogOpen}
        onClose={closeAddClientDialog}
        afterCreate={async () => {
          await refetch();
          closeAddClientDialog();
        }}
      />
    </>
  );
};

export default ClientList;
